import { TranslateService } from '@ngx-translate/core';
import { AvailableIntegrations } from '@/core/models/IntegrationEntity';

/**
 * @class IntegrationUtils
 *
 * Collection of helper methods for integrations. Mostly for complex translation handling.
 */
export class IntegrationUtils {
    /**
     * Returns the translation prefix for the given integration service.
     * Scoped translation format is: integrations.<service>.<key>
     * This method returns: integrations.<service>
     * Use for instance like this `${prefix}.name` to get the name of the integration
     * @param service The integration service to get the prefix for
     */
    public static getI18nPrefix(service: AvailableIntegrations): string {
        return this.getI18nPrefixString(service);
    }

    /**
     * Same as getI18nPrefix but accepts a string instead of AvailableIntegrations enum.
     * @param service The integration service to get the prefix for
     */
    public static getI18nPrefixString(service: string): string {
        return `integrations.${service}`;
    }

    /**
     * Returns the translation key for the given integration service and field.
     * Scoped translation format is: integrations.<service>.<key>
     * This method returns: integrations.<service>.<key>
     * @param service The integration service to get the key for
     * @param fieldKey The key refering the conrecte data like 'name' or 'card_description'
     * @returns The translation key
     */
    public static getIntKey(service: AvailableIntegrations, fieldKey: string): string {
        return this.getI18nPrefix(service) + '.' + fieldKey;
    }

    /**
     * Same gets the name key for the given integration service (string).
     * @param service The integration service to get the name key for.
     */
    public static getIntName(service: string): string {
        return this.getI18nPrefixString(service) + '.name';
    }

    /**
     * This returns a translated value like "name" or other integration specific i18n data.
     * @param translate TranslateService to be used for translation
     * @param service The integration service to get the translation for
     * @param translationKey The translation key of the integration service to be translated.
     * @returns The translated value
     */
    public static intTrans(
        translate: TranslateService,
        service: AvailableIntegrations,
        intTranslationKey: string,
    ): string {
        const prefix = this.getI18nPrefix(service);
        return translate.instant(prefix + '.' + intTranslationKey);
    }

    /**
     * Translates the given translation key with `integrationName` populated by the name of the provided integration service.
     *
     * @param translate TranslateService to be used for translation
     * @param service The integration service to be translated for (name retrieval)
     * @param translationKey The translation key to be translated.
     */
    public static transWithIntName(
        translate: TranslateService,
        service: AvailableIntegrations,
        translationKey: string,
    ): string {
        const integrationName = translate.instant(`integrations.${service}.name`);
        return translate.instant(translationKey, { integrationName });
    }

    public static getDetailsPageLink(serviceIdentifier: AvailableIntegrations): string {
        return '/settings/integrations/' + serviceIdentifier;
    }
}
